import {
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { Text, XStack, YStack } from 'tamagui';
import PSButton from '@/components/ui/PSButton';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSOtpInput from '@/components/ui/PSOtpInput';
import { PSErrorClass } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { useResendCountdown } from '@/src/hooks/useResendCountdown';
import { useAuthContext } from '@/src/lib/authContext';
import { startSession } from '@/src/lib/sessionStore';
import { toast } from '@tamagui/toast/v2';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';

const OTP_LENGTH = 6;

/**
 * Auth step 2 — verify the mobile OTP.
 *
 * Header is a ← BACK to `mobile` (gesture ON): going back with the number still
 * in the draft IS "change number" — there's no separate link.
 *
 * ── This is where the user becomes logged in ──
 * Confirming the SMS code is what creates the account server-side and mints the
 * token, so `startSession` happens HERE, not at the end of the flow — the `PATCH /me`
 * on the next screen is authenticated and can't be made without it. See
 * [[session]] for why "logged in with an incomplete profile" is a real state.
 *
 * ── Where we go next depends on the NAME, and on WHY we're here ──
 * We route on `areDetailsMissing` (no first name yet) rather than assuming
 * "verified mobile → details screen". A brand-new user has none, so details is
 * next; a returning user who already has one would otherwise be done logging
 * in here — email is no longer part of organic signup, so it's normally never
 * forced at this step regardless of whether it's missing or unverified. That
 * only gets asked for at booking time.
 *
 * "Normally" is the operative word: a `next=email` param (carried in from
 * `mobile.tsx`, itself carried in from a purchase/booking CTA — see
 * `app/events/[id]/index.tsx` and `app/memberships/purchase/confirm.tsx`)
 * means THIS login exists only to clear the way for a purchase, so stopping
 * here — or at a bare name — would just trade one interruption for a second
 * one a screen later. When it's set, a missing name still chains through
 * `details` (which forwards `next` itself), and an existing name with a
 * missing/unverified email skips straight to `email` instead of closing the
 * modal.
 */
export default function AuthVerifyMobile() {
  const router = useRouter();
  // The ROOT navigator — dismissing the whole auth modal happens here, not on
  // this nested stack (see the note on closeAuthModal below).
  const rootNavigation = useNavigation('/');
  const { mobile, markSent, otpSend } = useAuthContext();
  const { next, headerCopy } = useLocalSearchParams<{
    next?: string;
    headerCopy?: string;
  }>();
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | undefined>();
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const { remaining, canResend } = useResendCountdown('mobile', mobile);

  /**
   * `router.dismiss()` would act on the NEAREST stack — this group's own nested
   * Stack — and just pop one step. To leave the flow entirely we go up to the
   * ROOT navigator and `goBack()` there, which pops the `auth` modal entry off
   * the root stack and reveals whatever presented it. Caller-agnostic: no
   * hardcoded `/event` or `/` target, with a fallback for a deep link that
   * arrived straight into auth with nothing beneath it.
   */
  const closeAuthModal = () => {
    if (rootNavigation.canGoBack()) {
      rootNavigation.goBack();
    } else {
      router.replace('/');
    }
  };

  const verify = async (code: string) => {
    // No challenge means this screen was reached without one being issued (a deep
    // link straight to it). There's nothing to verify against, so send them back
    // to the step that issues one rather than failing silently.
    if (!otpSend.mobile?.challengeId) {
      toast.error('Please enter your mobile number again.');
      router.replace('/auth/mobile');
      return;
    }

    setVerifying(true);
    try {
      const user = await authApi.verifyOtp(otpSend.mobile.challengeId, code);

      await startSession(user);
      if (user.areDetailsMissing) {
        router.replace({
          pathname: '/auth/details',
          params: next ? { next, headerCopy } : undefined,
        });
      } else if (
        next === 'email' &&
        (user.isEmailMissing || user.isOnlyEmailVerificationPending)
      ) {
        router.replace({
          pathname: '/auth/email',
          params: headerCopy ? { headerCopy } : undefined,
        });
      } else {
        closeAuthModal();
      }
    } catch (err) {
      // `wrong_arguments` is the code, and ONLY the code: all four ways a code
      // fails (wrong, expired, already used, too many attempts) share it, and the
      // message says which. That verdict belongs on the field the user is looking
      // at, not in a toast floating over an empty error slot — and the
      // unrecoverable ones read as instructions to use the resend button below.
      //
      // Checked on `code`, not `kind === 'api'`: this endpoint also 404s for a
      // dead challenge and 400s on a malformed `challenge_id`, neither of which
      // is a judgment on the digits that were typed.
      //
      // TODO(auth): the backend is adding `field` to these errors. Once a code
      // failure arrives as `field: "otp"`, switch this to a field check — it says
      // the same thing more directly, and it's the same rule the other screens
      // use to decide inline-vs-toast.
      if (err instanceof PSErrorClass && err.code === 'wrong_arguments') {
        setOtpError(err.message);

        setOtp('');
      } else {
        toast.error(
          err instanceof PSErrorClass
            ? err.message
            : 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!otpSend.mobile?.challengeId) {
      toast.error('Please enter your mobile number again.');
      router.replace('/auth/mobile');
      return;
    }

    setResending(true);
    try {
      // The new id REPLACES the old one — the server destroys the previous
      // challenge, so keeping the id we came in with would fail the next verify
      // for no visible reason. Only stamp the countdown once the send succeeded.
      const newChallengeId = await authApi.resendOtp(
        otpSend.mobile.challengeId
      );
      markSent('mobile', mobile, newChallengeId);
      // The old code is dead, so clear the boxes rather than leave digits that
      // can no longer work sitting in them.
      setOtp('');
      setOtpError(undefined);
    } catch (err) {
      toast.error(
        err instanceof PSErrorClass
          ? err.message
          : 'Something went wrong. Please try again.'
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Head>
        <title>Verify mobile number · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <YStack px="$lg" pt="$xl" gap="$lg">
        <YStack gap="$xs">
          <Text fontSize="$large" fontWeight="$semibold" color="$textDark">
            Verify your number
          </Text>
          <XStack gap={'$space.xs'} flexWrap="wrap">
            <Text color="$textDarkMuted">we&apos;ve sent an otp on</Text>

            <Text fontWeight={'$semibold'}>{mobile}</Text>
          </XStack>
        </YStack>
        <PSOtpInput
          value={otp}
          onChangeText={setOtp}
          error={otpError}
          autoFocus
          onFocus={() => setOtpError(undefined)}
          // Verify the `code` argument, not `otp` state — it hasn't flushed yet
          // (see the atom docstring).
          onComplete={verify}
          disabled={verifying}
        />
        <XStack items="baseline" gap="$xs">
          <Text
            color="$textDarkMuted"
            // lineHeight={0}
          >
            Didn&apos;t get an otp?
          </Text>
          <PSLinkButton
            title={
              resending
                ? 'Sending…'
                : canResend
                  ? 'Resend'
                  : `Resend (in ${remaining} seconds)`
            }
            disabled={!canResend || resending}
            onPress={handleResend}
          />
        </XStack>
        <PSButton
          title="Verify"
          loading={verifying}
          loadingTitle="Verifying"
          onPress={() => verify(otp)}
          disabled={otp.length < OTP_LENGTH}
        />
      </YStack>
    </>
  );
}
